import { Eye, EyeOff, LoaderCircle, X } from 'lucide-react'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { useState } from 'react'

import { supabase } from '../../lib/supabase'
import { ROLES_USUARIO } from '../../utils/usuarios'

async function obtenerMensajeErrorFuncion(errorFuncion) {
  if (errorFuncion instanceof FunctionsHttpError) {
    try {
      const cuerpo = await errorFuncion.context.json()

      if (cuerpo?.error) return cuerpo.error
    } catch {
      // El cuerpo no era JSON válido, se usa el mensaje genérico.
    }
  }

  return (
    errorFuncion.message ||
    'No fue posible conectar con el servidor. Intenta nuevamente.'
  )
}

function CrearUsuarioModal({ empresaId, onClose, onGuardado }) {
  const [nombreCompleto, setNombreCompleto] = useState('')
  const [correo, setCorreo] = useState('')
  const [password, setPassword] = useState('')
  const [confirmarPassword, setConfirmarPassword] = useState('')
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [mostrarConfirmarPassword, setMostrarConfirmarPassword] =
    useState(false)
  const [rol, setRol] = useState('usuario')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const crearUsuario = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const nombreLimpio = nombreCompleto.trim()
    const correoLimpio = correo.trim()

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre completo.')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoLimpio)) {
      setError('Debes ingresar un correo electrónico válido.')
      return
    }

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.')
      return
    }

    if (password !== confirmarPassword) {
      setError('Las contraseñas no coinciden.')
      return
    }

    try {
      setGuardando(true)

      const { data, error: errorFuncion } =
        await supabase.functions.invoke('crear-usuario', {
          body: {
            nombre_completo: nombreLimpio,
            correo: correoLimpio,
            password,
            empresa_id: empresaId,
            rol,
          },
        })

      if (errorFuncion) {
        console.error('Error al crear usuario:', errorFuncion)
        setError(await obtenerMensajeErrorFuncion(errorFuncion))
        return
      }

      if (data?.error) {
        setError(data.error)
        return
      }

      onGuardado?.(data)
    } catch (errorCrear) {
      console.error('Error al crear usuario:', errorCrear)

      setError(
        errorCrear.message || 'No fue posible crear el usuario.'
      )
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            Crear usuario
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={crearUsuario}
        >
          <div>
            <label
              htmlFor="nombre_completo"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Nombre completo
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="nombre_completo"
              type="text"
              value={nombreCompleto}
              onChange={(event) =>
                setNombreCompleto(event.target.value)
              }
              placeholder="Nombre y apellido"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          <div>
            <label
              htmlFor="correo"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Correo
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="correo"
              type="email"
              value={correo}
              onChange={(event) => setCorreo(event.target.value)}
              placeholder="correo@empresa.cl"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Contraseña inicial
              <span className="ml-1 text-red-500">*</span>
            </label>

            <div className="relative">
              <input
                id="password"
                type={mostrarPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                placeholder="Mínimo 8 caracteres"
                disabled={guardando}
                className={`${estiloInput} pr-11`}
              />

              <button
                type="button"
                onClick={() => setMostrarPassword((valor) => !valor)}
                disabled={guardando}
                aria-label={
                  mostrarPassword
                    ? 'Ocultar contraseña'
                    : 'Mostrar contraseña'
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
              >
                {mostrarPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmar_password"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Confirmar contraseña
              <span className="ml-1 text-red-500">*</span>
            </label>

            <div className="relative">
              <input
                id="confirmar_password"
                type={mostrarConfirmarPassword ? 'text' : 'password'}
                value={confirmarPassword}
                onChange={(event) =>
                  setConfirmarPassword(event.target.value)
                }
                placeholder="Repite la contraseña"
                disabled={guardando}
                className={`${estiloInput} pr-11`}
              />

              <button
                type="button"
                onClick={() =>
                  setMostrarConfirmarPassword((valor) => !valor)
                }
                disabled={guardando}
                aria-label={
                  mostrarConfirmarPassword
                    ? 'Ocultar contraseña'
                    : 'Mostrar contraseña'
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
              >
                {mostrarConfirmarPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="rol"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Rol
            </label>

            <select
              id="rol"
              value={rol}
              onChange={(event) => setRol(event.target.value)}
              disabled={guardando}
              className={estiloInput}
            >
              {Object.entries(ROLES_USUARIO).map(
                ([valor, info]) => (
                  <option key={valor} value={valor}>
                    {info.etiqueta}
                  </option>
                )
              )}
            </select>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Creando...' : 'Crear usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default CrearUsuarioModal
